import UtilsSchema from "@repoflow/schema/utils";
import PageElementSchema from "@common/schema/page-element";
// `config` is `$ref: /PageConfig/Override`, so the document it names has to be
// in the same schema list — the same way a site's public shape spreads it.
import PageConfigSchema from "@common/schema/page-config";
// The file a translation group exports to and imports from. Spread here rather
// than kept in a list of its own so that `/Public/Page/Import` below can `$ref`
// it — a document is only resolvable from the array it was added with.
import PageBundleSchema from "./bundle";

import FieldsSchema, {
  siteKey,
  orgId,
  siteId,
  pageId,

  title,
  translateTitle,
  route,
  locale,
  translationKey,
  elements,
  config,
  editor,
  published,
  createdAt,
  updatedAt
} from "./fields";

export default [
  ...UtilsSchema,
  ...PageElementSchema,
  ...PageConfigSchema,
  ...PageBundleSchema,
  ...FieldsSchema,
  {
    id: "/Public/Page",
    type: "object",
    properties: {
      siteKey,
      orgId,
      siteId,
      pageId,
      title,
      route,
      locale,
      translationKey,
      elements,
      config,
      editor,
      // What is served, as the API answers it — see PagePublished in
      // api/code/src/schema/account/page. On the read shape so a screen's
      // value object keeps them through extraction, and on NO input shape:
      // nothing but publish and rollback moves the pointer.
      published,
      served: { type: "boolean" },
      changed: { type: "boolean" },
      createdAt,
      updatedAt
    }
  },
  {
    // No `config` here, and no `elements`, for the same reason: a page is named
    // and routed on the create form and composed on its own screen. A shell
    // override before there is anything to wrap is a decision nobody can make
    // yet — the model seeds the empty one.
    id: "/Public/Page/Create",
    type: "object",
    properties: {
      title,
      route,
      locale,
      translationKey,
    },
    // Non-empty on purpose. An empty `required` array is what made assertValid
    // unable to reject anything in the project this shape came from (A9 in
    // docs/datum-api.md). `title` is already `String!` on PageInputCreate, so
    // this cannot reject a request GraphQL would have accepted — it only stops
    // the model layer being reachable with less than the contract promises.
    required: ["title"],
  },
  {
    // Making a page exist in another language.
    //
    // Not a Create with more fields on it: what distinguishes this is the
    // SOURCE. The elements and the shell override are copied from the page this
    // is invoked on, server-side, because that is where the row already is —
    // sending a page's whole content back up to create a copy of it would put
    // a document on the wire twice for no reason and would let the two diverge
    // in flight.
    //
    // `route` is optional because the server can usually derive it: the site
    // declares a prefix per locale, so the Spanish translation of `/pricing` is
    // `/es/pricing` without anybody typing it. An author who wants
    // `/es/precios` — which is most of the point of the feature — says so.
    //
    // `title` here is `translateTitle` and not the shared `title`, and that is
    // load-bearing rather than tidy: the shared one carries `minLength: 1`,
    // which is right for a page being NAMED and wrong for a field whose empty
    // value means "copy the source's". A form validates its whole value object,
    // so the shared field made this form invalid from the moment it mounted.
    // See the comment on `translateTitle` in ./fields.
    id: "/Public/Page/Translate",
    type: "object",
    properties: {
      locale,
      route,
      title: translateTitle,
    },
    required: ["locale"],
  },
  {
    // Which kept version to serve instead of the live one. The one input a
    // rollback takes: the draft is deliberately not touched, so there is
    // nothing else to say. See api/code/src/model/page/publish.
    id: "/Public/Page/Rollback",
    type: "object",
    properties: {
      versionId: { type: "string", minLength: 1 },
    },
    required: ["versionId"],
  },
  {
    id: "/Public/Page/Update",
    type: "object",
    properties: {
      title,
      route,
      locale,
      translationKey,
      elements,
      config,
      editor,
    },
    required: ["title"],
  },
  {
    // Putting an edited bundle back.
    //
    // The bundle is the whole of the input and `dryRun` is the whole of the
    // options, which is deliberate: every question an import could be
    // parameterised by — which pages, into which site, what to do with one the
    // file does not mention — is answered by the file itself or by a refusal.
    // The one thing a caller decides is whether this run writes.
    //
    // `dryRun` costs the same reads and makes the same refusals as the real
    // thing and then stops, so the plan a screen shows is produced by the code
    // that would do the work rather than by a second implementation of it.
    id: "/Public/Page/Import",
    type: "object",
    properties: {
      bundle: { $ref: "/Page/Bundle" },
      dryRun: { type: "boolean", default: false },
    },
    required: ["bundle"],
  },
];
